import React, { FunctionComponent, useEffect, useState } from 'react';
import { Container, DialogContent, DialogTitle, IconButton, makeStyles, Typography } from '@material-ui/core';
import BackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import { JournalEntry, JournalEntryList } from '../../store/modules/accounting/types';
import InvoiceList from './InvoiceList';
import Invoice from './Invoice';
import { Person } from 'src/store/modules/person/types';
import { License, MergeTemplate } from 'src/store/modules/application/types';

type InvoicesDialogProps = {
    person: Person;
    license?: License;
    onClose: Function;
    journalEntries: JournalEntryList;
    journalEntriesLoading: boolean;
    getJournalEntries: Function;
    downloadInvoices: Function;
    voidInvoice: Function;
    mergeTemplatesList: MergeTemplate[];
    getMergeTemplatesBegin: Function;
    mergeDocuments: Function;
};

const useStyles = makeStyles((theme) => ({
    dialog: {
        backgroundColor: theme.palette.background.default,
    },
    dialogHeader: {
        display: 'flex',
        alignItems: 'baseline',
    },
    dialogSpacer: {
        flexGrow: 1,
    },
    backButton: {
        padding: theme.spacing(1),
        marginTop: -theme.spacing(1),
        marginBottom: -theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    closeButton: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadius: 4,
        padding: 0,
    },
}));

const InvoicesDialog: FunctionComponent<InvoicesDialogProps> = (props) => {
    const {
        person,
        license,
        onClose,
        getJournalEntries,
        journalEntries,
        journalEntriesLoading,
        downloadInvoices,
        voidInvoice,
        mergeTemplatesList,
        getMergeTemplatesBegin,
        mergeDocuments,
    } = props;
    const classes = useStyles();

    const [selectedInvoice, setSelectedInvoice] = useState<JournalEntry>();

    const licenseId = license?.id;
    const personId = person?.id;

    useEffect(() => {
        getJournalEntries({
            filter: {
                where: {
                    type: 'Invoice',
                    [licenseId ? 'lineItems.license.id' : 'lineItems.person.id']: licenseId ?? personId,
                },
            },
        });
    }, [personId, licenseId, getJournalEntries, selectedInvoice]);

    return (
        <>
            <DialogTitle>
                <div className={classes.dialogHeader}>
                    <Typography variant="h5">
                        {!selectedInvoice && 'Invoices'}
                        {selectedInvoice && (
                            <>
                                <IconButton
                                    onClick={() => setSelectedInvoice(undefined)}
                                    className={classes.backButton}
                                >
                                    <BackIcon />
                                </IconButton>
                                {selectedInvoice.invoicing?.type.name} #{selectedInvoice.invoicing?.invoiceNumber}
                            </>
                        )}
                    </Typography>
                    <div className={classes.dialogSpacer} />
                    <IconButton onClick={() => onClose()} className={classes.closeButton}>
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent className={classes.dialog}>
                {!selectedInvoice && (
                    <Container maxWidth="sm">
                        <InvoiceList
                            invoices={journalEntries.sort(compareByDate)}
                            loading={journalEntriesLoading}
                            licenseId={licenseId}
                            personId={personId}
                            onSelect={setSelectedInvoice}
                            downloadInvoices={downloadInvoices}
                            voidInvoice={voidInvoice}
                            getMergeTemplatesBegin={getMergeTemplatesBegin}
                            mergeTemplatesList={mergeTemplatesList}
                            mergeDocuments={mergeDocuments}
                        />
                    </Container>
                )}
                {selectedInvoice && (
                    <Container maxWidth="lg">
                        <Invoice invoice={selectedInvoice} person={person} license={license} />
                    </Container>
                )}
            </DialogContent>
        </>
    );
};

function compareByDate(invoice1: JournalEntry, invoice2: JournalEntry): number {
    return invoice2.date.localeCompare(invoice1.date);
}

export default InvoicesDialog;
